import React from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import { ShareModel } from "common247";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedSegmentedControl } from "@/components/ThemedSegmentedControl";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { ThemedEntityLink } from "@/components/ThemedEntityLink";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColor } from "@/hooks/useThemeColor";
import { CustomAlert } from "@/components/ThemedAlert";

/**
 * Props for the ShareEditor component.
 */
export interface IShareEditorProps {
  share: ShareModel;
  canEdit?: boolean;
  canRevoke?: boolean;
  isSaving?: boolean;
  isRevoking?: boolean;
  onRoleChange: (newRole: "viewer" | "editor") => Promise<void> | void;
  onRevoke: () => Promise<void> | void;
  onNavigateToSource?: () => void;
  onNavigateToTarget?: () => void;
  onCancel?: () => void;
}

/**
 * Standalone editor component for viewing and managing a single Share record.
 * Aligned with the item edit screens UX standards (modular ThemedView panels, 13px bold headers, and bottom action bar).
 */
export function ShareEditor({
  share,
  canEdit = true,
  canRevoke = true,
  isSaving = false,
  isRevoking = false,
  onRoleChange,
  onRevoke,
  onNavigateToSource,
  onNavigateToTarget,
  onCancel,
}: IShareEditorProps): React.JSX.Element {
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const tintColor = useThemeColor({}, "tint");
  const destructiveColor = "#d93025";

  const sourceTypeName =
    share.sourceType.charAt(0).toUpperCase() + share.sourceType.slice(1);
  const targetTypeName =
    share.targetType.charAt(0).toUpperCase() + share.targetType.slice(1);

  const handleRevokePress = () => {
    CustomAlert.alert(
      "Revoke Access",
      `Are you sure you want to revoke access to "${share.sourceDisplayName || "this item"}" for ${share.targetDisplayName || "this recipient"}?`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Revoke Access",
          style: "destructive",
          onPress: () => onRevoke(),
        },
      ]
    );
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      {/* 1. Header Summary Card Panel */}
      <ThemedView style={styles.section}>
        <View style={styles.headerRow}>
          <View style={styles.headerTitleArea}>
            <ThemedEntityLink
              type={share.sourceType}
              id={share.sourceId}
              displayName={share.sourceDisplayName || share.sourceId}
              variant="title"
              numberOfLines={2}
              textStyle={styles.headerTitle}
            />
            <ThemedText style={styles.headerSubtitle}>
              Shared with {share.targetDisplayName || share.targetId} ({targetTypeName}) • {share.role === "editor" ? "Editor Access" : "Viewer Access"}
            </ThemedText>
          </View>
          <View
            style={[
              styles.roleBadge,
              { backgroundColor: share.role === "editor" ? "rgba(74, 144, 226, 0.15)" : badgeBg },
            ]}
          >
            <ThemedText
              type="stamp"
              style={[
                styles.roleBadgeText,
                share.role === "editor" ? { color: tintColor } : null,
              ]}
            >
              {share.role.toUpperCase()}
            </ThemedText>
          </View>
        </View>
      </ThemedView>

      {/* 2. Shared Resource (Source Item) Panel */}
      <ThemedView style={styles.section}>
        <View style={styles.sectionHeader}>
          <EntityIcon type={share.sourceType} size={16} color={tintColor} style={styles.sectionIcon} />
          <ThemedText style={styles.sectionTitle}>
            Shared {sourceTypeName}
          </ThemedText>
        </View>

        <View style={styles.itemDetailRow}>
          <View style={{ flex: 1 }}>
            <ThemedEntityLink
              type={share.sourceType}
              id={share.sourceId}
              displayName={share.sourceDisplayName || share.sourceId}
              showOpenIcon={true}
              onPress={onNavigateToSource}
              textStyle={styles.itemDisplayName}
              numberOfLines={2}
            />
            <ThemedText style={styles.itemIdText}>
              ID: {share.sourceId}
            </ThemedText>
          </View>
        </View>
      </ThemedView>

      {/* 3. Recipient (Target Contact / Group) Panel */}
      <ThemedView style={styles.section}>
        <View style={styles.sectionHeader}>
          <EntityIcon type={share.targetType} size={16} color={tintColor} style={styles.sectionIcon} />
          <ThemedText style={styles.sectionTitle}>
            Recipient ({targetTypeName})
          </ThemedText>
        </View>

        <View style={styles.itemDetailRow}>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
              <ThemedEntityLink
                type={share.targetType}
                id={share.targetId}
                displayName={share.targetDisplayName || share.targetId}
                showOpenIcon={true}
                onPress={onNavigateToTarget}
                textStyle={styles.itemDisplayName}
                numberOfLines={2}
              />
              {share.targetUserId && (
                <View style={styles.verifiedBadge}>
                  <Ionicons name="checkmark-circle" size={13} color="#1e8e3e" />
                  <ThemedText style={styles.verifiedText}>App User</ThemedText>
                </View>
              )}
            </View>
            <ThemedText style={styles.itemIdText}>
              ID: {share.targetId}
            </ThemedText>
          </View>
        </View>
      </ThemedView>

      {/* 4. Permission Role Configuration Panel */}
      <ThemedView style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="shield-checkmark-outline" size={16} color={tintColor} style={styles.sectionIcon} />
          <ThemedText style={styles.sectionTitle}>
            Access Role & Permissions
          </ThemedText>
          {isSaving && (
            <ThemedText style={styles.savingIndicatorText}>Saving...</ThemedText>
          )}
        </View>

        <ThemedText style={styles.sectionDescription}>
          Configure what access privileges {share.targetDisplayName || "the recipient"} has for this item.
        </ThemedText>

        <View style={styles.segmentedControlWrapper}>
          <ThemedSegmentedControl
            value={share.role}
            size="medium"
            readOnly={!canEdit || isSaving}
            onChange={(newRole) => {
              if (newRole === "viewer" || newRole === "editor") {
                onRoleChange(newRole);
              }
            }}
            options={[
              {
                icon: "eye-outline",
                label: "Viewer",
                value: "viewer",
                accessibilityLabel: "Viewer Access",
              },
              {
                icon: "pencil-outline",
                label: "Editor",
                value: "editor",
                accessibilityLabel: "Editor Access",
              },
            ]}
          />
        </View>

        <View style={[styles.roleExplanationBox, { backgroundColor: badgeBg }]}>
          {share.role === "editor" ? (
            <View style={styles.roleExplanationContent}>
              <Ionicons name="pencil" size={14} color={tintColor} style={{ marginTop: 2 }} />
              <ThemedText style={styles.roleExplanationText}>
                <ThemedText style={{ fontWeight: "bold" }}>Editor:</ThemedText> Recipient can view, edit, update content, and collaborate on this resource.
              </ThemedText>
            </View>
          ) : (
            <View style={styles.roleExplanationContent}>
              <Ionicons name="eye" size={14} color="#1e8e3e" style={{ marginTop: 2 }} />
              <ThemedText style={styles.roleExplanationText}>
                <ThemedText style={{ fontWeight: "bold" }}>Viewer:</ThemedText> Recipient has read-only access. They cannot edit or re-share this resource.
              </ThemedText>
            </View>
          )}
        </View>
      </ThemedView>

      {/* 5. Audit & Metadata Panel */}
      <ThemedView style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="information-circle-outline" size={16} color={tintColor} style={styles.sectionIcon} />
          <ThemedText style={styles.sectionTitle}>
            Share Details
          </ThemedText>
        </View>

        <View style={styles.metadataGrid}>
          <View style={styles.metadataItem}>
            <ThemedText style={styles.metadataLabel}>Date Shared</ThemedText>
            <ThemedText style={styles.metadataValue}>
              {share.sharedAt ? new Date(share.sharedAt).toLocaleString() : "Unknown"}
            </ThemedText>
          </View>
          <View style={styles.metadataItem}>
            <ThemedText style={styles.metadataLabel}>Share ID</ThemedText>
            <ThemedText style={styles.metadataValue} numberOfLines={1}>
              {share.id || "N/A"}
            </ThemedText>
          </View>
          {share.sharedBy ? (
            <View style={styles.metadataItem}>
              <ThemedText style={styles.metadataLabel}>Shared By (Owner)</ThemedText>
              <ThemedText style={styles.metadataValue} numberOfLines={1}>
                {share.sharedBy}
              </ThemedText>
            </View>
          ) : null}
        </View>
      </ThemedView>

      {/* 6. Revoke Access / Danger Zone Panel */}
      {canRevoke && (
        <ThemedView style={[styles.section, styles.dangerSection]}>
          <View style={styles.sectionHeader}>
            <Ionicons name="warning-outline" size={16} color={destructiveColor} style={styles.sectionIcon} />
            <ThemedText style={[styles.sectionTitle, { color: destructiveColor }]}>
              Revoke Share Access
            </ThemedText>
          </View>

          <ThemedText style={styles.sectionDescription}>
            Revoking access immediately disconnects {share.targetDisplayName || "the recipient"} from this resource.
          </ThemedText>

          <ThemedButton
            title="Revoke Access"
            variant="danger"
            loading={isRevoking}
            disabled={isRevoking}
            icon={<Ionicons name="trash-outline" size={14} color="#FFFFFF" />}
            onPress={handleRevokePress}
            style={styles.revokeBtn}
          />
        </ThemedView>
      )}

      {/* Bottom Action Footer Row aligned with item edit screens */}
      <View style={styles.footer}>
        <View style={styles.leftFooterControls}>
          <ThemedButton title="Back" variant="minimal" onPress={onCancel} />
        </View>
        <View style={styles.rightFooterControls}>
          {canRevoke && (
            <ThemedButton
              title="Revoke Access"
              variant="danger"
              loading={isRevoking}
              disabled={isRevoking}
              icon={<Ionicons name="trash-outline" size={14} color="#FFFFFF" />}
              onPress={handleRevokePress}
              style={styles.actionBtn}
            />
          )}
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 8,
    gap: 8,
    paddingBottom: 24,
  },
  section: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 0,
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.05)",
  },
  dangerSection: {
    backgroundColor: "rgba(217, 48, 37, 0.03)",
    borderColor: "rgba(217, 48, 37, 0.25)",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  headerTitleArea: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: "700",
    letterSpacing: -0.3,
  },
  headerSubtitle: {
    fontSize: 11,
    opacity: 0.65,
    marginTop: 2,
  },
  roleBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 0,
    alignSelf: "flex-start",
  },
  roleBadgeText: {
    fontSize: 9,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  sectionIcon: {
    marginRight: 6,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 4,
    flex: 1,
  },
  savingIndicatorText: {
    fontSize: 10,
    color: "#1e8e3e",
    fontWeight: "bold",
  },
  sectionDescription: {
    fontSize: 11,
    opacity: 0.7,
    marginBottom: 8,
    lineHeight: 15,
  },
  itemDetailRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  itemDisplayName: {
    fontSize: 13,
    fontWeight: "600",
  },
  itemIdText: {
    fontSize: 9.5,
    opacity: 0.45,
    fontFamily: "monospace",
    marginTop: 2,
  },
  actionLinkBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderWidth: 1,
    borderRadius: 0,
    backgroundColor: "rgba(0,0,0,0.02)",
  },
  actionLinkText: {
    fontSize: 11,
    fontWeight: "600",
  },
  verifiedBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    backgroundColor: "#e6f4ea",
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
  },
  verifiedText: {
    fontSize: 8.5,
    color: "#1e8e3e",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  segmentedControlWrapper: {
    marginVertical: 4,
  },
  roleExplanationBox: {
    marginTop: 8,
    padding: 8,
    borderRadius: 0,
  },
  roleExplanationContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  roleExplanationText: {
    fontSize: 11,
    lineHeight: 15,
    flex: 1,
    opacity: 0.85,
  },
  metadataGrid: {
    gap: 6,
  },
  metadataItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 2,
  },
  metadataLabel: {
    fontSize: 10.5,
    opacity: 0.55,
  },
  metadataValue: {
    fontSize: 11,
    fontWeight: "500",
  },
  revokeBtn: {
    marginTop: 4,
    alignSelf: "flex-start",
  },
  footer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 16,
    paddingBottom: 24,
  },
  leftFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  rightFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  actionBtn: {
    minWidth: 140,
  },
});

